import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  addLine,
  cancelSale,
  checkout,
  parkSale,
  removeLine,
  startSale,
  updateLine,
  updateHeader,
  setCustomer,
  addAdvance,
  removeAdvance,
} from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import SageToolbar from "@/components/SageToolbar";
import ProductFinder from "@/components/ProductFinder";
import PaymentModal from "@/components/PaymentModal";
import TicketPreview from "@/components/TicketPreview";
import CustomerPicker from "@/components/CustomerPicker";
import {
  Plus,
  Trash2,
  Banknote,
  CreditCard,
  ChevronDown,
} from "lucide-react";

const RIGHT_TABS = [
  { key: "factura", label: "Factura" },
  { key: "vales",   label: "Vales / Crédito" },
  { key: "cashlogic", label: "Cash Logic" },
  { key: "datos",   label: "Datos" },
];
const BOTTOM_TABS = [
  { key: "cliente",      label: "Cliente" },
  { key: "pagos",        label: "Pagos" },
  { key: "observaciones", label: "Observaciones" },
  { key: "importes",     label: "Importes" },
  { key: "comisiones",   label: "Comisiones" },
  { key: "factura",      label: "Factura" },
  { key: "precios",      label: "Precios" },
  { key: "portes",       label: "Portes" },
  { key: "divisas",      label: "Divisas" },
  { key: "envios",       label: "Envíos" },
];

export default function Mostrador() {
  const navigate = useNavigate();
  const { user, session, refreshSession, refreshChain, refreshSage } = useApp();
  const [sale, setSale] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [lastTicket, setLastTicket] = useState(null);
  const [finderOpen, setFinderOpen] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [rightTab, setRightTab] = useState("factura");
  const [bottomTab, setBottomTab] = useState("cliente");

  // ensure session
  useEffect(() => {
    if (!session) navigate("/cash");
  }, [session, navigate]);

  const ensureSale = useCallback(async () => {
    if (sale) return sale;
    if (!session) return null;
    const s = await startSale({}, user, session.id);
    setSale(s);
    return s;
  }, [sale, session, user]);

  useEffect(() => {
    if (!sale && session) ensureSale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // ---------- handlers ----------

  const pickProduct = async ({ product, variant }) => {
    const s = await ensureSale();
    if (!s) return;
    const updated = await addLine(s.id, {
      product_id: product.id,
      variant_id: variant?.id,
      qty: 1,
    });
    setSale(updated);
    setFinderOpen(false);
    toast.success(`${product.name}${variant?.talla ? ` · ${variant.talla}` : ""} añadido`);
  };

  const changeLine = async (line, patch) => {
    const updated = await updateLine(sale.id, line.line_id, patch);
    setSale(updated);
  };
  const dropLine = async (line) => {
    const updated = await removeLine(sale.id, line.line_id);
    setSale(updated);
  };

  const onHeaderChange = async (patch) => {
    const updated = await updateHeader(sale.id, patch);
    setSale(updated);
  };

  const onPickCustomer = async (c) => {
    setCustOpen(false);
    const s = await ensureSale();
    if (!s) return;
    const updated = await setCustomer(s.id, c?.id);
    setSale(updated);
  };

  const onClear = async () => {
    if (!sale) return;
    if (!window.confirm("¿Anular el ticket actual?")) return;
    await cancelSale(sale.id);
    setSale(null);
    ensureSale();
  };
  const onPark = async () => {
    if (!sale?.lines?.length) return;
    await parkSale(sale.id);
    toast.success("Ticket aparcado");
    setSale(null);
    ensureSale();
  };
  const onCobrar = () => {
    if (!sale?.lines?.length) return toast.error("Añade al menos una línea");
    setPayOpen(true);
  };

  const onConfirmPay = async (payments) => {
    try {
      const r = await checkout(sale.id, { payments, customer_id: sale.customer_id || undefined, doc_type: "ticket" });
      setLastTicket(r);
      setPayOpen(false);
      setSale(null);
      refreshChain();
      refreshSage();
      toast.success(`Ticket ${r.serie}/${r.numero} cobrado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cobrar");
    }
  };

  const onFacturar = async () => {
    if (!sale?.lines?.length) return toast.error("Sin líneas");
    setPayOpen(true);
  };

  const newTicket = () => {
    setLastTicket(null);
    setSale(null);
    ensureSale();
    refreshSession();
  };

  const onAddAdvance = async (method, amount) => {
    const updated = await addAdvance(sale.id, { method, amount });
    setSale(updated);
    toast.success("Entrega registrada");
  };
  const onRemoveAdvance = async (adv) => {
    const updated = await removeAdvance(sale.id, adv.id);
    setSale(updated);
  };

  const onToolbar = (action) => {
    switch (action) {
      case "new":      newTicket(); break;
      case "delete":   onClear(); break;
      case "validate": onCobrar(); break;
      case "refresh":  refreshSession(); ensureSale(); break;
      case "search":   setFinderOpen(true); break;
      case "customer": setCustOpen(true); break;
      case "cobro":    onCobrar(); break;
      case "print":    if (lastTicket) window.print(); break;
      default: break;
    }
  };

  const total = sale?.total || 0;
  const pendiente = Math.max(total - (sale?.advances_total || 0), 0);
  const today = new Date().toLocaleDateString("es-ES");
  const ejercicio = new Date().getFullYear();

  return (
    <div className="h-full flex flex-col bg-[#F0F0F0]">
      {/* Title bar Sage */}
      <div className="h-7 shrink-0 bg-[#0F172A] text-white px-3 flex items-center justify-between text-sm">
        <span className="font-medium">VENTAS Mostrador VP · {user?.name} · {session?.till_id}</span>
        <span className="text-white/60 text-xs">? · ✕</span>
      </div>
      {/* Menu bar */}
      <div className="h-6 shrink-0 bg-[#F5F5F5] border-b border-slate-300 px-2 flex items-center gap-4 text-xs text-slate-700">
        <span><u>V</u>entas</span>
        <span><u>E</u>dición</span>
        <span><u>P</u>rocesos</span>
        <span>Man<u>t</u>enimientos</span>
      </div>
      <SageToolbar onAction={onToolbar} />

      {/* Header strip */}
      <div className="bg-[#F5F5F5] border-b border-slate-300 px-2 py-2 grid grid-cols-12 gap-2">
        {/* Datos venta */}
        <div className="col-span-5 border border-slate-300 bg-white p-2">
          <Legend>Datos Ventas Mostrador VP</Legend>
          <div className="grid grid-cols-[60px_1fr_60px_1fr] gap-x-2 gap-y-1 text-xs items-center mt-1">
            <Label>Ejercicio</Label>
            <SInput data-testid={TID.sageEjercicio} value={ejercicio} readOnly />
            <Label>Serie</Label>
            <SInput data-testid={TID.sageSerie} value={sale?.serie || "T"} readOnly />
            <Label>Fecha</Label>
            <SInput data-testid={TID.sageFecha} value={today} readOnly />
            <Label>Nº</Label>
            <SInput data-testid={TID.sageNumero} value={sale?.numero || 0} readOnly />
            <Label>Cliente</Label>
            <div className="col-span-3 flex gap-1">
              <SInput data-testid={TID.sageCliente} value={sale?.customer_name || ""} readOnly placeholder="(sin cliente)" />
              <button
                data-testid={TID.sageCambiarClienteBtn}
                onClick={() => setCustOpen(true)}
                className="h-6 px-2 text-[11px] border border-pink-400 bg-pink-50 hover:bg-pink-100 text-pink-700 whitespace-nowrap"
              >Cambiar Cliente</button>
            </div>
            <Label>Nº lín</Label>
            <SInput data-testid={TID.sageNumLineas} value={sale?.lines?.length ?? 0} readOnly />
            <Label>NIF</Label>
            <SInput value={sale?.customer_nif || ""} readOnly />
          </div>
        </div>

        {/* Forma cobro */}
        <div className="col-span-3 border border-slate-300 bg-white p-2">
          <Legend>Fecha y Forma de Cobro</Legend>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <SInput data-testid={TID.sageFormaCobro} value="now" readOnly className="text-center" />
            <SInput value="98" readOnly className="text-center" />
          </div>
          <button
            data-testid={TID.sageCobrarBtn}
            onClick={onCobrar}
            disabled={!sale?.lines?.length}
            className="mt-2 w-full h-11 bg-[#0F172A] hover:bg-slate-800 disabled:bg-slate-400 text-white font-display font-semibold text-lg tracking-wide border-2 border-pink-400 disabled:border-slate-400 transition-colors"
          >
            COBRAR
          </button>
          <div className="mt-2 text-center text-[11px] text-slate-600 font-mono">**IMPORTE PENDIENTE**</div>
          <div data-testid={TID.sageImportePendiente} className="text-center font-mono text-lg text-slate-900">
            {pendiente.toFixed(2)}
          </div>
        </div>

        {/* Importe venta + tabs derecha */}
        <div className="col-span-4 border border-slate-300 bg-white p-2 flex flex-col">
          <Legend>{"**********IMPORTE VENTA**********"}</Legend>
          <div data-testid={TID.sageImporteVenta} className="font-mono text-3xl font-bold text-slate-900 text-right pr-2 mt-1">
            {total.toFixed(2)} €
          </div>
          <div className="text-[10px] text-right text-slate-500 mt-0.5 font-mono">
            Base {sale?.subtotal?.toFixed(2) || "0.00"} · IVA {sale?.tax_total?.toFixed(2) || "0.00"}
          </div>

          {/* Right tabs */}
          <div className="mt-2 flex border-b border-slate-300">
            {RIGHT_TABS.map((t) => (
              <button
                key={t.key}
                data-testid={TID.sageTabRight(t.key)}
                onClick={() => setRightTab(t.key)}
                className={`px-2 py-1 text-[11px] border-r border-slate-300 ${
                  rightTab === t.key ? "bg-white border-t-2 border-t-pink-500 -mb-px" : "bg-[#E6E6E6] text-slate-600"
                }`}
              >{t.label}</button>
            ))}
          </div>
          <div className="flex-1 bg-white border border-t-0 border-slate-300 p-2 text-[12px]">
            <RightTabContent
              tab={rightTab}
              sale={sale}
              onFacturar={onFacturar}
              onCobrar={onCobrar}
            />
          </div>
        </div>
      </div>

      {/* Lines grid */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-1 min-h-0">
        <div className="border border-slate-300 bg-white flex-1 flex flex-col min-h-0">
          <div className="h-7 bg-[#E6E6E6] border-b border-slate-300 flex items-center px-2 text-[11px] text-slate-700 font-medium">
            <span>Líneas del documento</span>
            <button
              data-testid={TID.sageAddProductBtn}
              onClick={() => setFinderOpen(true)}
              className="ml-auto h-5 px-2 bg-emerald-600 text-white text-[11px] hover:bg-emerald-700 flex items-center gap-1"
            >
              <Plus size={12} /> Añadir artículo (F2)
            </button>
          </div>
          <div className="flex-1 overflow-auto thin-scroll">
            <table className="w-full text-[12px] font-mono">
              <thead className="bg-[#F0F0F0] text-slate-700 text-[11px] sticky top-0">
                <tr className="border-b border-slate-300">
                  <th className="text-left  px-2 py-1.5 font-medium w-[80px]">SKU</th>
                  <th className="text-left  px-2 py-1.5 font-medium">Artículo</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[60px]">Uds.</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[80px]">PVP</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[60px]">Dto.%</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[80px]">Importe</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[60px]">% IVA</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[80px]">Cuota IVA</th>
                  <th className="text-right px-2 py-1.5 font-medium w-[80px]">Total línea</th>
                  <th className="w-[34px]"></th>
                </tr>
              </thead>
              <tbody>
                {(sale?.lines || []).map((ln, idx) => (
                  <tr
                    key={ln.line_id}
                    data-testid={TID.sageLine(ln.line_id)}
                    className={`border-b border-slate-100 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50`}
                  >
                    <td className="px-2 py-1 text-slate-500">{ln.sku}</td>
                    <td className="px-2 py-1 font-sans">
                      {ln.name}
                      {ln.is_suplido && <span className="ml-2 text-[10px] px-1 bg-amber-100 text-amber-700 rounded">SUPLIDO</span>}
                      {ln.recargo_pct > 0 && <span className="ml-2 text-[10px] px-1 bg-blue-100 text-blue-700 rounded">REC {(ln.recargo_pct*100).toFixed(1)}%</span>}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        data-testid={TID.sageLineQty(ln.line_id)}
                        type="number" min={0.01} step={ln.unit === "kg" ? 0.01 : 1}
                        value={ln.qty}
                        onChange={(e) => changeLine(ln, { qty: parseFloat(e.target.value || "0") })}
                        className="w-full h-6 text-right bg-transparent border border-transparent hover:border-slate-300 focus:bg-white focus:border-slate-500 focus:outline-none px-1"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">{ln.unit_price.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right">
                      <input
                        data-testid={TID.sageLineDto(ln.line_id)}
                        type="number" min={0} max={100} step={1}
                        value={ln.discount_pct}
                        onChange={(e) => changeLine(ln, { discount_pct: parseFloat(e.target.value || "0") })}
                        className="w-full h-6 text-right bg-transparent border border-transparent hover:border-slate-300 focus:bg-white focus:border-slate-500 focus:outline-none px-1"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">{ln.importe_neto?.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right text-slate-500">{(ln.tax_rate * 100).toFixed(0)}%</td>
                    <td className="px-2 py-1 text-right">{ln.cuota_iva?.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right font-semibold">{ln.subtotal?.toFixed(2)}</td>
                    <td className="px-1 py-1 text-center">
                      <button
                        data-testid={TID.sageLineDelete(ln.line_id)}
                        onClick={() => dropLine(ln)}
                        className="h-6 w-6 text-red-600 hover:bg-red-50 flex items-center justify-center"
                      ><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
                {(!sale?.lines || sale.lines.length === 0) && (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-slate-400 text-sm font-sans">
                      Pulse <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded text-[10px] mx-1">F2</kbd> o el botón "+ Añadir artículo" para iniciar la venta.
                    </td>
                  </tr>
                )}
              </tbody>
              {sale?.lines?.length > 0 && (
                <tfoot className="bg-[#F0F0F0] sticky bottom-0 text-[11px] font-semibold">
                  <tr className="border-t border-slate-400">
                    <td colSpan={5} className="px-2 py-1 text-right text-slate-600">TOTALES</td>
                    <td className="px-2 py-1 text-right">{sale.subtotal?.toFixed(2)}</td>
                    <td></td>
                    <td className="px-2 py-1 text-right">{sale.tax_total?.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right text-base">{sale.total?.toFixed(2)} €</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Bottom tabs */}
      <div className="shrink-0 px-2 pb-2">
        <div className="flex border-b border-slate-300">
          {BOTTOM_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={TID.sageTabBottom(t.key)}
              onClick={() => setBottomTab(t.key)}
              className={`px-3 py-1 text-[11px] border-r border-slate-300 ${
                bottomTab === t.key ? "bg-white border-t-2 border-t-pink-500 -mb-px text-slate-900" : "bg-[#E6E6E6] text-slate-600"
              }`}
            >{t.label}</button>
          ))}
        </div>
        <div className="border border-t-0 border-slate-300 bg-white p-3 min-h-[140px] max-h-[200px] overflow-auto thin-scroll">
          <BottomTabContent
            tab={bottomTab}
            sale={sale}
            onHeaderChange={onHeaderChange}
            onAddAdvance={onAddAdvance}
            onRemoveAdvance={onRemoveAdvance}
            onPark={onPark}
          />
        </div>
      </div>

      {/* Modals */}
      {finderOpen && (
        <ProductFinder onClose={() => setFinderOpen(false)} onPick={pickProduct} />
      )}
      {payOpen && (
        <PaymentModal sale={sale} onClose={() => setPayOpen(false)} onConfirm={onConfirmPay} pendiente={pendiente} />
      )}
      {lastTicket && (
        <TicketPreview sale={lastTicket} onClose={() => setLastTicket(null)} onNew={newTicket} />
      )}
      {custOpen && (
        <CustomerPicker
          currentId={sale?.customer_id}
          onClose={() => setCustOpen(false)}
          onPick={onPickCustomer}
        />
      )}
    </div>
  );
}

// ---------- Helpers ----------

function Legend({ children }) {
  return <div className="text-[11px] text-slate-600 -mt-3 -ml-1 bg-white px-1 inline-block">{children}</div>;
}
function Label({ children }) {
  return <span className="text-[11px] text-slate-600 text-right pr-1">{children}</span>;
}
function SInput({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`h-6 px-1.5 text-[12px] bg-[#F8F8F8] border border-slate-300 font-mono focus:bg-white focus:border-slate-500 focus:outline-none disabled:opacity-60 ${className}`}
    />
  );
}

// ---------- Right tab content ----------
function RightTabContent({ tab, sale, onFacturar, onCobrar }) {
  if (tab === "factura") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-[40px_1fr_40px_1fr] gap-1 items-center">
          <Label>Nº</Label>
          <SInput value={sale?.numero || 0} readOnly />
          <Label>Serie</Label>
          <SInput value={sale?.serie || "T"} readOnly />
        </div>
        <div>
          <Label>Máscara factura</Label>
          <SInput value="T-{NUMERO:06d}" readOnly className="w-full" />
        </div>
        <div className="flex gap-1">
          <button
            data-testid={TID.sageFacturarBtn}
            onClick={onFacturar}
            disabled={!sale?.lines?.length}
            className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-[11px] font-bold tracking-wide border-2 border-pink-400 disabled:border-slate-300"
          >
            FACTURAR+IMPRIMIR+COBRAR
          </button>
          <button className="h-9 px-3 border-2 border-pink-400 bg-white text-[11px] font-bold tracking-wide hover:bg-slate-50">
            VER FRA
          </button>
        </div>
      </div>
    );
  }
  if (tab === "vales") {
    return (
      <div className="text-slate-500">
        <div className="font-medium mb-1">Vales / Crédito</div>
        <div className="text-[11px]">Saldo cliente: <span className="font-mono">0.00€</span></div>
        <div className="text-[11px]">Vales emitidos: 0 · Vales consumidos: 0</div>
        <button className="mt-2 h-8 px-3 border border-slate-300 bg-white text-xs hover:bg-slate-50">Emitir vale por importe</button>
      </div>
    );
  }
  if (tab === "cashlogic") {
    return (
      <div className="text-slate-500">
        <div className="font-medium mb-1">Cash Logic / Cashlogy</div>
        <div className="text-[11px]">Estado: <span className="text-amber-600">No conectado (modo demo)</span></div>
        <div className="text-[11px]">Sólo disponible en cliente desktop.</div>
      </div>
    );
  }
  if (tab === "datos") {
    return (
      <div className="text-slate-500 text-[11px] font-mono">
        <div>ID interno: {sale?.id?.slice(0, 8) || "—"}…</div>
        <div>Sage: {sale?.sage_status} {sale?.sage_doc_ref && `· ${sale.sage_doc_ref}`}</div>
        <div>Estado: {sale?.status}</div>
        <div>Creado: {sale?.created_at?.slice(0, 19).replace("T", " ")}</div>
      </div>
    );
  }
  return null;
}

// ---------- Bottom tab content ----------
function BottomTabContent({ tab, sale, onHeaderChange, onAddAdvance, onRemoveAdvance, onPark }) {
  if (tab === "cliente") {
    return (
      <div className="grid grid-cols-4 gap-x-3 gap-y-1.5 text-[11px]">
        <div className="col-span-4 grid grid-cols-[100px_1fr] gap-1 items-center">
          <Label>Nombre cial.</Label>
          <SInput value={sale?.customer_name || ""} readOnly />
        </div>
        <div className="grid grid-cols-[100px_1fr] gap-1 items-center"><Label>NIF/CIF</Label><SInput value={sale?.customer_nif || ""} readOnly /></div>
        <div className="grid grid-cols-[100px_1fr] gap-1 items-center"><Label>Régimen</Label><SInput value={sale?.customer_regime || "normal"} readOnly /></div>
        <div className="grid grid-cols-[100px_1fr] gap-1 items-center"><Label>Nación</Label><SInput value="108 · España" readOnly /></div>
        <div className="grid grid-cols-[100px_1fr] gap-1 items-center"><Label>Cód. postal</Label><SInput value="" readOnly /></div>
      </div>
    );
  }
  if (tab === "pagos") {
    return (
      <div className="text-[12px]">
        <div className="font-medium mb-2">Entregas a cuenta</div>
        {(sale?.advances || []).length === 0 ? (
          <div className="text-slate-500 text-[11px]">Sin entregas registradas. Recoge un anticipo antes de cerrar el ticket.</div>
        ) : (
          <table className="w-full text-[11px] font-mono mb-2">
            <thead className="bg-slate-100">
              <tr><th className="px-2 py-1 text-left">Fecha</th><th className="text-left">Método</th><th className="text-right">Importe</th><th></th></tr>
            </thead>
            <tbody>
              {sale.advances.map((a) => (
                <tr key={a.id} className="border-b border-slate-100">
                  <td className="px-2 py-1">{a.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="capitalize">{a.method}</td>
                  <td className="text-right">{a.amount.toFixed(2)}€</td>
                  <td className="text-center"><button onClick={() => onRemoveAdvance(a)} className="text-red-600 hover:underline text-[10px]">Quitar</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex gap-1 mt-2 items-center">
          <span className="text-[11px] text-slate-600">Nueva entrega:</span>
          <button onClick={() => askAdvance("efectivo", onAddAdvance)} className="h-7 px-2 text-[11px] border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-1"><Banknote size={12} /> Efectivo</button>
          <button onClick={() => askAdvance("tarjeta", onAddAdvance)}  className="h-7 px-2 text-[11px] border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-1"><CreditCard size={12} /> Tarjeta</button>
          <button onClick={() => askAdvance("bizum", onAddAdvance)}    className="h-7 px-2 text-[11px] border border-slate-300 bg-white hover:bg-slate-50">Bizum</button>
          <button onClick={onPark} className="ml-auto h-7 px-2 text-[11px] border border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-700">Aparcar</button>
        </div>
      </div>
    );
  }
  if (tab === "observaciones") {
    return (
      <textarea
        defaultValue={sale?.notes || ""}
        onBlur={(e) => onHeaderChange({ notes: e.target.value })}
        placeholder="Notas internas, observaciones del cliente…"
        className="w-full h-28 p-2 border border-slate-300 text-[12px] font-mono"
      />
    );
  }
  if (tab === "importes") {
    return (
      <div className="grid grid-cols-2 gap-3 text-[12px]">
        <div className="space-y-1">
          <Row k="Importe bruto"     v={sale?.bruto_total} />
          <Row k="Total descuentos"  v={sale?.discount_total} />
          <Row k="Base imponible"    v={sale?.subtotal} />
          <Row k="Total IVA + Rec."  v={sale?.tax_total} />
          <Row k="Punto Verde"       v={sale?.envases_total} />
          <Row k="Suplidos"          v={sale?.suplidos_total} />
          <Row k="TOTAL LÍQUIDO"     v={sale?.total} bold />
        </div>
        <div className="space-y-1">
          <PctRow k="% Dto. cliente"  v={sale?.customer_discount_pct} onChange={(v) => onHeaderChange({ customer_discount_pct: v })} />
          <PctRow k="% Rappel"        v={sale?.rappel_pct}            onChange={(v) => onHeaderChange({ rappel_pct: v })} />
          <PctRow k="% Pronto pago"   v={sale?.prompt_payment_pct}    onChange={(v) => onHeaderChange({ prompt_payment_pct: v })} />
        </div>
      </div>
    );
  }
  if (tab === "portes") {
    return (
      <div className="grid grid-cols-4 gap-x-3 gap-y-2 text-[12px]">
        <EurRow k="Portes"  v={sale?.portes}  onChange={(v) => onHeaderChange({ portes: v })} />
        <EurRow k="Fletes"  v={sale?.fletes}  onChange={(v) => onHeaderChange({ fletes: v })} />
        <EurRow k="Seguro"  v={sale?.seguro}  onChange={(v) => onHeaderChange({ seguro: v })} />
        <EurRow k="Aduana"  v={sale?.aduana}  onChange={(v) => onHeaderChange({ aduana: v })} />
      </div>
    );
  }
  if (tab === "factura") {
    return (
      <div className="text-[11px] text-slate-600 space-y-1">
        <div>Tipo documento: <b>{sale?.doc_type || "ticket"}</b></div>
        <div>Verifactu: <b>{sale?.verifactu?.mode || "VERIFACTU"}</b></div>
        <div>Mención: <b>{sale?.verifactu?.mencion || "—"}</b></div>
        <div>Huella actual: <code className="text-[10px]">{sale?.verifactu?.huella_actual?.slice(0, 32) || "—"}…</code></div>
      </div>
    );
  }
  if (tab === "precios") {
    return <div className="text-[11px] text-slate-500">Tarifa aplicada: <b>Pública</b> · Divisa: <b>EUR</b></div>;
  }
  if (tab === "comisiones") {
    return <div className="text-[11px] text-slate-500">Comisiones por línea (vendedor 1-4, jefe ventas, jefe zona) — gestionadas desde Sage 200.</div>;
  }
  if (tab === "divisas") {
    return <div className="text-[11px] text-slate-500">Divisa: <b>EUR · €</b> · Cambio: <code>1.000000</code></div>;
  }
  if (tab === "envios") {
    return <div className="text-[11px] text-slate-500">Sin envío asociado a este ticket de mostrador.</div>;
  }
  return null;
}

function Row({ k, v, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "border-t border-slate-300 pt-1 font-semibold" : ""}`}>
      <span className="text-slate-600">{k}</span>
      <span className="font-mono">{Number(v || 0).toFixed(2)} €</span>
    </div>
  );
}
function PctRow({ k, v, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-600">{k}</span>
      <div className="flex items-center gap-1">
        <input
          type="number" min={0} max={100} step={0.1}
          value={v ?? 0}
          onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
          className="w-20 h-6 px-1.5 text-right font-mono border border-slate-300 focus:outline-none focus:border-slate-500"
        />
        <span className="text-slate-500">%</span>
      </div>
    </div>
  );
}
function EurRow({ k, v, onChange }) {
  return (
    <div>
      <div className="text-[11px] text-slate-600">{k}</div>
      <div className="flex items-center gap-1 mt-0.5">
        <input
          type="number" min={0} step={0.01}
          value={v ?? 0}
          onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
          className="w-full h-7 px-1.5 text-right font-mono border border-slate-300 focus:outline-none focus:border-slate-500"
        />
        <span className="text-slate-500">€</span>
      </div>
    </div>
  );
}

function askAdvance(method, onAdd) {
  const amt = window.prompt(`Importe de la entrega a cuenta (${method})`, "0.00");
  const v = parseFloat((amt || "0").replace(",", "."));
  if (v > 0) onAdd(method, v);
}
